<template>
  <v-sheet class="appointments" rounded="lg" outlined>
    <!-- Фильтры -->
    <div class="appointments__filters">
      <v-text-field
        v-model="search"
        placeholder="Поиск: имя, email, Telegram"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        hide-details
        clearable
        class="appointments__search"
      />
      <v-select
        v-if="expertOptions.length > 1"
        v-model="expertId"
        :items="expertOptions"
        placeholder="Все эксперты"
        outlined
        dense
        hide-details
        clearable
        class="appointments__select"
      />
      <v-select
        v-if="serviceOptions.length > 1"
        v-model="serviceName"
        :items="serviceOptions"
        placeholder="Все услуги"
        outlined
        dense
        hide-details
        clearable
        class="appointments__select"
      />
      <v-btn-toggle v-model="period" mandatory dense class="appointments__period">
        <v-btn small value="upcoming">Предстоящие</v-btn>
        <v-btn small value="past">Прошедшие</v-btn>
        <v-btn small value="all">Все</v-btn>
      </v-btn-toggle>
    </div>

    <div class="appointments__statuses">
      <v-chip
        v-for="option in statusOptions"
        :key="option.value"
        small
        :outlined="status !== option.value"
        :color="status === option.value ? '#483CB9' : undefined"
        :text-color="status === option.value ? 'white' : undefined"
        class="mr-2 mb-2"
        @click="status = option.value"
      >
        {{ option.label }}
        <span class="appointments__status-count">{{ statusCounts[option.value] }}</span>
      </v-chip>
      <v-chip v-if="month" small close color="#ede9ff" text-color="#483CB9" class="mr-2 mb-2" @click:close="month = null">
        <v-icon x-small left>mdi-calendar-month-outline</v-icon>
        {{ monthLabel }}
      </v-chip>
    </div>

    <div
      v-if="status !== 'pending' && awaitingCount"
      class="appointments__attention"
    >
      <v-icon small color="#B26A00" class="mr-2">mdi-alert-circle-outline</v-icon>
      {{ pendingText }}
      <v-btn x-small text color="#B26A00" class="text-none ml-auto" @click="status = 'pending'">Показать</v-btn>
    </div>

    <!-- Список по дням -->
    <div v-if="!filtered.length" class="appointments__empty">
      <template v-if="hasFilters">
        Ничего не найдено.
        <v-btn small text color="#483CB9" class="text-none" @click="resetFilters">Сбросить фильтры</v-btn>
      </template>
      <template v-else>{{ emptyText }}</template>
    </div>

    <div v-else>
      <div class="appointments__head">
        <span>Время</span>
        <span>Клиент</span>
        <span>Услуга</span>
        <span>Эксперт</span>
        <span>Заявка</span>
        <span>Статус</span>
      </div>
      <div v-for="group in groups" :key="group.date" class="appointments__group">
        <div class="appointments__day">
          <span class="appointments__day-label">{{ group.label }}</span>
          <span class="appointments__day-count">{{ group.items.length }}</span>
        </div>
        <div
          v-for="item in group.items"
          :key="item.id"
          :class="['appointment-row', `appointment-row--${item.status}`]"
          role="button"
          tabindex="0"
          @click="openDetails(item.id)"
          @keydown.enter="openDetails(item.id)"
        >
          <div class="appointment-row__time">{{ timeRange(item) }}</div>
          <div class="appointment-row__client">
            <div class="appointment-row__name text-truncate">{{ guestName(item) }}</div>
            <div class="appointment-row__contacts text-truncate">
              {{ [item.guest_email, item.telegram_username].filter(Boolean).join(' · ') }}
            </div>
          </div>
          <div class="appointment-row__service text-truncate">{{ item.service_type_name }}</div>
          <div class="appointment-row__expert">
            <template v-if="expertOf(item)">
              <v-avatar size="24" color="#483CB9" class="mr-2 flex-shrink-0">
                <img v-if="expertOf(item).photo_url" :src="expertOf(item).photo_url" alt="">
                <span v-else class="white--text appointment-row__initials">{{ initialsOf(expertNameOf(item)) }}</span>
              </v-avatar>
              <span class="text-truncate">{{ expertNameOf(item) }}</span>
            </template>
            <span v-else class="grey--text">—</span>
          </div>
          <div class="appointment-row__created" :title="createdTitle(item)">{{ createdShort(item) }}</div>
          <div class="appointment-row__status">
            <template v-if="item.status === 'pending'">
              <v-chip v-if="isPast(item)" x-small color="#f0f0f0" text-color="#666" class="mr-1" title="Время записи прошло — подтвердить уже нельзя">
                Просрочена
              </v-chip>
              <v-btn
                v-else
                x-small
                depressed
                color="#009E60"
                dark
                class="mr-1"
                :loading="actionLoading"
                title="Подтвердить с постоянной ссылкой эксперта"
                @click.stop="quickAccept(item)"
              >
                Подтвердить
              </v-btn>
              <v-btn x-small icon color="#EC2E2E" title="Отклонить" :disabled="actionLoading" @click.stop="$emit('decline-appointment', item.id)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </template>
            <v-chip v-else x-small :color="statusMetaOf(item).color" :text-color="statusMetaOf(item).textColor">
              {{ statusMetaOf(item).label }}
            </v-chip>
          </div>
        </div>
      </div>

      <div v-if="filtered.length > visibleCount" class="text-center mt-4">
        <v-btn small outlined color="#483CB9" class="text-none" @click="visibleCount += pageSize">
          Показать ещё ({{ filtered.length - visibleCount }})
        </v-btn>
      </div>
    </div>

    <!-- Подробности записи -->
    <v-navigation-drawer
      v-model="drawer"
      fixed
      temporary
      right
      :width="$vuetify.breakpoint.xsOnly ? '100%' : 420"
      class="appointment-drawer"
    >
      <div v-if="selected" class="appointment-drawer__body">
        <div class="d-flex align-center justify-space-between mb-3">
          <v-chip small :color="statusMetaOf(selected).color" :text-color="statusMetaOf(selected).textColor">
            {{ statusMetaOf(selected).label }}
          </v-chip>
          <v-btn icon small @click="drawer = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <div class="appointment-drawer__title">{{ selected.service_type_name }}</div>
        <div class="appointment-drawer__when">{{ fullDate(selected) }}, {{ timeRange(selected) }}</div>

        <div class="appointment-drawer__section">
          <div class="appointment-drawer__label">Клиент</div>
          <div class="font-weight-medium">{{ guestName(selected) }}</div>
          <div v-if="selected.guest_email">
            <a :href="`mailto:${selected.guest_email}`">{{ selected.guest_email }}</a>
          </div>
          <div v-if="selected.telegram_username">Telegram: {{ selected.telegram_username }}</div>
        </div>

        <div class="appointment-drawer__section">
          <div class="appointment-drawer__label">Эксперт</div>
          <div class="d-flex align-center">
            <template v-if="expertOf(selected)">
              <v-avatar size="28" color="#483CB9" class="mr-2 flex-shrink-0">
                <img v-if="expertOf(selected).photo_url" :src="expertOf(selected).photo_url" alt="">
                <span v-else class="white--text text-caption">{{ initialsOf(expertNameOf(selected)) }}</span>
              </v-avatar>
              <span class="text-truncate">{{ expertNameOf(selected) }}</span>
            </template>
            <span v-else class="grey--text">Не указан</span>
            <v-btn
              v-if="canChangeExpert(selected)"
              x-small
              text
              color="#483CB9"
              class="text-none ml-auto"
              :disabled="actionLoading"
              @click="changeExpertOpen = true"
            >
              <v-icon x-small left>mdi-account-switch-outline</v-icon>
              Изменить
            </v-btn>
          </div>
        </div>

        <div v-if="selected.created_at" class="appointment-drawer__section">
          <div class="appointment-drawer__label">Заявка создана</div>
          {{ createdTitle(selected) }}
        </div>

        <AppointmentCard
          :key="`${selected.id}-${selected.status}`"
          :appointment="selected"
          :default-conference-url="defaultConferenceUrl(selected)"
          :loading="actionLoading"
          actions-only
          class="mt-4"
          @accept="$emit('accept-appointment', $event)"
          @decline="$emit('decline-appointment', $event)"
          @edit-conference-link="$emit('edit-conference-link', $event)"
        />

        <!-- Внутри drawer: клик в диалоге не считается кликом «снаружи» и не закрывает его. -->
        <ChangeExpertDialog
          v-model="changeExpertOpen"
          :appointment="selected"
          :experts="experts"
          :services="services"
          :service-types="serviceTypes"
          :availability="availability"
          :loading="actionLoading"
          :errors="changeExpertErrors"
          @save="$emit('change-expert', { id: selected.id, expert: $event })"
          @clear-errors="$emit('clear-change-expert-errors')"
        />
      </div>
    </v-navigation-drawer>
  </v-sheet>
</template>

<script>
import { utcToLocalDate, utcToLocalTime } from '@/common/calendarTime';
import AppointmentCard from './AppointmentCard.vue';
import ChangeExpertDialog from './ChangeExpertDialog.vue';
import {
  appointmentExpertId, expertFullName, guestFullName, initials, isAwaitingConfirmation, isCancelledStatus,
  isPastAppointment, statusMeta,
} from './appointmentHelpers';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Активные' },
  { value: 'pending', label: 'Ожидают' },
  { value: 'confirmed', label: 'Подтверждены' },
  { value: 'cancelled', label: 'Отклонённые и отменённые' },
  { value: 'all', label: 'Все' },
];

const matchesStatus = (item, status) => {
  if (status === 'all') return true;
  if (status === 'active') return !isCancelledStatus(item.status);
  if (status === 'cancelled') return isCancelledStatus(item.status);
  return item.status === status;
};

const pluralize = (n, forms) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
};

export default {
  name: 'AppointmentsList',
  components: { AppointmentCard, ChangeExpertDialog },
  props: {
    appointments: { type: Array, default: () => [] },
    experts: { type: Array, default: () => [] },
    services: { type: Array, default: () => [] },
    serviceTypes: { type: Array, default: () => [] },
    availability: { type: Array, default: () => [] },
    changeExpertErrors: { type: Array, default: () => [] },
    // Счётчик успешных смен эксперта: изменился — смена прошла, закрываем диалог.
    changeExpertDone: { type: Number, default: 0 },
    actionLoading: { type: Boolean, default: false },
    // Фильтр, выставленный снаружи (переход из статистики или панели эксперта):
    // { status, month, expertId, period, appointmentId, nonce }.
    preset: { type: Object, default: null },
  },
  data() {
    return {
      statusOptions: STATUS_OPTIONS,
      search: '',
      expertId: null,
      serviceName: null,
      period: 'upcoming', // upcoming | past | all
      status: 'active',
      month: null, // YYYY-MM — только при переходе из статистики
      pageSize: 30,
      visibleCount: 30,
      drawer: false,
      selectedId: null,
      changeExpertOpen: false,
    };
  },
  computed: {
    expertOptions() {
      const ids = [...new Set(this.appointments.map((item) => appointmentExpertId(item, this.services)))];
      return ids
        .map((id) => ({ value: id, text: expertFullName(this.expertById(id)) }))
        .filter((option) => option.text)
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    serviceOptions() {
      return [...new Set(this.appointments.map((item) => item.service_type_name).filter(Boolean))].sort();
    },
    monthLabel() {
      return this.month ? this.$moment(this.month, 'YYYY-MM').locale('ru').format('MMMM YYYY') : '';
    },
    hasFilters() {
      return Boolean(this.search || this.expertId || this.serviceName || this.month)
        || this.period !== 'upcoming' || this.status !== 'active';
    },
    // Все фильтры, кроме статуса, — от них считаются счётчики на чипах статусов.
    baseFiltered() {
      const now = this.$moment();
      const query = (this.search || '').trim().toLowerCase();
      return this.appointments.filter((item) => {
        if (this.month) {
          if (!utcToLocalDate(item.scheduled_starts_at).startsWith(this.month)) return false;
        } else if (this.period !== 'all') {
          const isPast = this.$moment.utc(item.scheduled_ends_at).isBefore(now);
          if ((this.period === 'past') !== isPast) return false;
        }
        if (this.expertId && appointmentExpertId(item, this.services) !== this.expertId) return false;
        if (this.serviceName && item.service_type_name !== this.serviceName) return false;
        if (query) {
          const haystack = [guestFullName(item), item.guest_email, item.telegram_username]
            .filter(Boolean).join(' ').toLowerCase();
          if (!haystack.includes(query)) return false;
        }
        return true;
      });
    },
    statusCounts() {
      return STATUS_OPTIONS.reduce((acc, option) => {
        acc[option.value] = this.baseFiltered.filter((item) => matchesStatus(item, option.value)).length;
        return acc;
      }, {});
    },
    // Прошедшие — от последних к ранним, остальное — по хронологии.
    filtered() {
      const direction = this.period === 'past' && !this.month ? -1 : 1;
      return this.baseFiltered
        .filter((item) => matchesStatus(item, this.status))
        .sort((a, b) => direction * a.scheduled_starts_at.localeCompare(b.scheduled_starts_at));
    },
    groups() {
      const groups = [];
      this.filtered.slice(0, this.visibleCount).forEach((item) => {
        const date = utcToLocalDate(item.scheduled_starts_at);
        let group = groups[groups.length - 1];
        if (!group || group.date !== date) {
          group = { date, label: this.dayLabel(date), items: [] };
          groups.push(group);
        }
        group.items.push(item);
      });
      return groups;
    },
    // Просроченные ожидающие заявки подтвердить нельзя — в плашку «ждут подтверждения» не идут.
    awaitingCount() {
      return this.baseFiltered.filter(isAwaitingConfirmation).length;
    },
    pendingText() {
      const n = this.awaitingCount;
      return `${n} ${pluralize(n, ['заявка ждёт', 'заявки ждут', 'заявок ждут'])} подтверждения`;
    },
    emptyText() {
      return this.period === 'past' ? 'Прошедших записей нет.' : 'Предстоящих записей пока нет.';
    },
    selected() {
      return this.appointments.find((item) => item.id === this.selectedId) || null;
    },
  },
  watch: {
    preset: {
      immediate: true,
      handler(preset) {
        if (preset) this.applyPreset(preset);
      },
    },
    changeExpertDone() {
      this.changeExpertOpen = false;
    },
    drawer(isOpen) {
      if (!isOpen) this.changeExpertOpen = false;
    },
    changeExpertOpen(isOpen) {
      if (isOpen) this.$emit('clear-change-expert-errors');
    },
    search() { this.visibleCount = this.pageSize; },
    expertId() { this.visibleCount = this.pageSize; },
    serviceName() { this.visibleCount = this.pageSize; },
    status() { this.visibleCount = this.pageSize; },
    month() { this.visibleCount = this.pageSize; },
    period(value) {
      this.visibleCount = this.pageSize;
      if (value !== 'all') this.month = null;
    },
  },
  methods: {
    applyPreset({
      status, month, expertId, period, appointmentId,
    }) {
      this.search = '';
      this.expertId = expertId || null;
      this.serviceName = null;
      this.status = status || 'active';
      this.month = month || null;
      this.period = period || 'all';
      if (appointmentId) this.openDetails(appointmentId);
    },
    resetFilters() {
      this.search = '';
      this.expertId = null;
      this.serviceName = null;
      this.month = null;
      this.period = 'upcoming';
      this.status = 'active';
    },
    openDetails(id) {
      this.selectedId = id;
      this.drawer = true;
    },
    expertById(id) {
      return this.experts.find((expert) => expert.id === id) || null;
    },
    expertOf(item) {
      return this.expertById(appointmentExpertId(item, this.services));
    },
    expertNameOf(item) {
      return expertFullName(this.expertOf(item));
    },
    initialsOf(name) {
      return initials(name);
    },
    guestName(item) {
      return guestFullName(item) || 'Без имени';
    },
    statusMetaOf(item) {
      return statusMeta(item.status);
    },
    isPast(item) {
      return isPastAppointment(item);
    },
    // Менять эксперта имеет смысл только у действующей записи, которая ещё не закончилась.
    canChangeExpert(item) {
      return !isCancelledStatus(item.status) && this.$moment.utc(item.scheduled_ends_at).isAfter(this.$moment());
    },
    defaultConferenceUrl(item) {
      const expert = this.expertOf(item);
      return (expert && expert.permanent_conference_url) || '';
    },
    // Быстрое подтверждение — как кнопка в карточке без правки ссылки: уходит постоянная ссылка эксперта.
    quickAccept(item) {
      this.$emit('accept-appointment', { id: item.id, conference_url: this.defaultConferenceUrl(item) });
    },
    timeRange(item) {
      return `${utcToLocalTime(item.scheduled_starts_at)} – ${utcToLocalTime(item.scheduled_ends_at)}`;
    },
    fullDate(item) {
      return this.$moment.utc(item.scheduled_starts_at).local().locale('ru').format('dddd, D MMMM YYYY');
    },
    dayLabel(date) {
      const day = this.$moment(date, 'YYYY-MM-DD').locale('ru');
      const today = this.$moment().startOf('day');
      const diff = day.diff(today, 'days');
      const sameYear = day.year() === today.year();
      const base = day.format(sameYear ? 'D MMMM' : 'D MMMM YYYY');
      if (diff === 0) return `Сегодня, ${base}`;
      if (diff === 1) return `Завтра, ${base}`;
      if (diff === -1) return `Вчера, ${base}`;
      const weekday = day.format('dd');
      return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${base}`;
    },
    createdShort(item) {
      if (!item.created_at) return '—';
      return this.$moment.utc(item.created_at).local().locale('ru').format('D MMM, HH:mm');
    },
    createdTitle(item) {
      if (!item.created_at) return '';
      return this.$moment.utc(item.created_at).local().locale('ru').format('D MMMM YYYY, HH:mm');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../_tokens.scss';

$columns: 104px minmax(160px, 1.4fr) minmax(120px, 1fr) minmax(140px, 1fr) 110px 190px;

.appointments {
  padding: $space-4;

  &__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: $space-2;
    margin-bottom: $space-3;
  }

  &__search {
    flex: 1 1 240px;
    max-width: 320px;
  }

  &__select {
    flex: 0 1 200px;
  }

  &__period {
    margin-left: auto;

    .v-btn {
      text-transform: none;
    }
  }

  &__status-count {
    margin-left: 6px;
    font-weight: $weight-semibold;
    opacity: 0.8;
  }

  &__attention {
    display: flex;
    align-items: center;
    background: #fff4e0;
    color: #B26A00;
    font-size: $text-sm;
    border-radius: $radius-sm;
    padding: 6px $space-3;
    margin-bottom: $space-3;
  }

  &__empty {
    color: #8a8f9c;
    font-size: $text-sm;
    text-align: center;
    padding: 40px 8px;
  }

  &__head {
    display: grid;
    grid-template-columns: $columns;
    column-gap: $space-3;
    padding: 0 $space-3 $space-2 15px;
    font-size: 11px;
    font-weight: $weight-semibold;
    color: #8a8f9c;
    text-transform: uppercase;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  &__day {
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: $space-2;
    background: #fff;
    padding: $space-3 0 6px;
  }

  &__day-label {
    font-size: $text-sm;
    font-weight: $weight-semibold;
  }

  &__day-count {
    font-size: 11px;
    color: #8a8f9c;
    background: #f2f3f5;
    border-radius: $radius-full;
    padding: 0 8px;
  }
}

.appointment-row {
  display: grid;
  grid-template-columns: $columns;
  column-gap: $space-3;
  align-items: center;
  min-height: 52px;
  padding: 6px $space-3;
  margin-bottom: 4px;
  font-size: $text-sm;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-left: 3px solid #483CB9;
  border-radius: $radius-sm;
  cursor: pointer;
  transition: background-color $transition-fast;

  &:hover, &:focus-visible {
    background: #f7f6fd;
    outline: none;
  }

  &--pending { border-left-color: #F0A030; }
  &--confirmed { border-left-color: #009E60; }

  &--declined, &--cancelled_by_guest {
    border-left-color: #c4c7cf;
    color: #8a8f9c;
  }

  &__time {
    font-weight: $weight-semibold;
    white-space: nowrap;
  }

  &__client, &__service, &__expert {
    min-width: 0;
  }

  &__name {
    font-weight: $weight-medium;
  }

  &__contacts, &__created {
    font-size: $text-xs;
    color: #8a8f9c;
  }

  &__expert {
    display: flex;
    align-items: center;
  }

  &__initials {
    font-size: 10px;
  }

  &__status {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}

// Узкий экран: строка превращается в компактную карточку.
@media (max-width: 959px) {
  .appointments__head {
    display: none;
  }

  .appointments__period {
    margin-left: 0;
  }

  .appointment-row {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      'time status'
      'client client'
      'service expert'
      'created created';
    row-gap: 4px;
    padding: 10px $space-3;

    &__time { grid-area: time; }
    &__status { grid-area: status; }
    &__client { grid-area: client; }
    &__service { grid-area: service; color: #5f6472; }
    &__expert { grid-area: expert; justify-content: flex-end; }

    &__created {
      grid-area: created;

      &::before {
        content: 'Заявка: ';
      }
    }
  }
}

.appointment-drawer__body {
  padding: $space-4 $space-5;
}

.appointment-drawer__title {
  font-size: $text-lg;
  font-weight: $weight-semibold;
}

.appointment-drawer__when {
  color: #5f6472;
  font-size: $text-sm;
  margin-bottom: $space-2;

  &::first-letter {
    text-transform: uppercase;
  }
}

.appointment-drawer__section {
  margin-top: $space-4;
  font-size: $text-sm;
}

.appointment-drawer__label {
  font-size: 11px;
  font-weight: $weight-semibold;
  color: #8a8f9c;
  text-transform: uppercase;
  margin-bottom: $space-1;
}
</style>
